/* eslint-disable max-lines */
import React, { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import Table, { TableItem, TableItemHeader, TableItemValue } from '../Table';
import moment from 'moment-jalaali';
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import * as LabActions from '../../../redux/Laboratory/LaboratoryAction';

// import LabDialog from './LabDialog';
import { AlertDialog, Options } from '../../UI';
import { useContext } from 'react';
import MessagesContext from '../../../contexts/messagesContext';
import { useDispatch, useSelector } from 'react-redux';
import useCheckAccess from '../../../hooks/useCheckAccess';

const useStyle = makeStyles((theme) => ({
    container: {
        position: 'relative',
        display: 'flex'
    },
    item: {
        width: 100,
        position: 'relative'
    },
    value: {
        fontSize: 14,
        width: '100%',
        cursor: 'text',
        textAlign: 'center'
    },
    moreButton: {
        width: '100%',
        fontWeight: 'bold',
        textAlign: 'center',
        '&:hover': {
            color: '#009be5',
            background: 'white'
        }
    },
    table: {
        justifyContent: 'start !important',
        '&:after': {
            content: '" "',
            paddingRight: 150
        }
    },
    navigateNext: ({ disableNext }) => ({
        position: 'absolute',
        top: '50%',
        right: -10,
        background: 'white',
        transform: 'translateY(-50%)',
        zIndex: 2,
        color: theme.palette.primary.light,
        boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
        width: 30,
        height: 30,
        transition: '0.5s ease all',
        pointerEvents: disableNext ? 'none' : undefined,
        opacity: disableNext ? 0 : 1,
        '&:hover': {
            background: 'white',
            boxShadow: '0px 0px 15px rgba(0,0,0,0.2)'
        },
        [theme.breakpoints.down(1025)]: {
            right: 0
        }
    }),
    navigatePrev: ({ disablePrev }) => ({
        position: 'absolute',
        top: '50%',
        left: -10,
        background: 'white',
        transform: 'translateY(-50%)',
        zIndex: 2,
        color: theme.palette.primary.light,
        boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
        width: 30,
        height: 30,
        transition: '0.5s ease all',
        '&:hover': {
            background: 'white',
            boxShadow: '0px 0px 15px rgba(0,0,0,0.2)'
        },
        pointerEvents: disablePrev ? 'none' : undefined,
        opacity: disablePrev ? 0 : 1
    }),
    icons: {
        width: 20,
        height: 20
    },
    input: {
        width: 80,
        height: '80%',
        borderRadius: 6,
        border: '1px solid #333',
        textAlign: 'center'
    },
    optionsContainer: {
        position: 'relative',
        '& div:first-child': {
            inset: 'unset'
        }
    }
}));

function Rows({ data: labTest, handleSaveValue }) {
    const location = useLocation();
    const tableRef = useRef();
    const [editableValue, setEditableValue] = useState('');
    const [inputValue, setInputValue] = useState({ value: '', for: null });
    const [scrollLeft, setScrollLeft] = useState(0);
    const [delResultId, setDelResultId] = useState(null);
    const { notEditClinical } = useCheckAccess();
    const resultsGroup = useSelector((state) =>
        state.laboratory.laboratoryTestGroups.find((group) => group.id === labTest.id)
    );
    const [dialog, setDialog] = useState(false);
    const classes = useStyle({
        disableNext: !(labTest?.results?.length - 1),
        disablePrev: Math.round(scrollLeft / 200) === 0
    });
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();
    const { messages } = useContext(MessagesContext);
    const options = [
        { label: messages.edit, icon: 'edit', value: 0 },
        { label: messages.delete, icon: 'delete', value: 1 }
    ];
    useLayoutEffect(() => {
        if (tableRef.current?.scrollTo) {
            tableRef.current.scrollTo(-tableRef?.current?.scrollWidth, 0);
        }
    }, []);

    const handleToggleValueInput =
        ({ resultId, valueId, value, editableValue }) =>
        () => {
            if (!notEditClinical) {
                if (editableValue === valueId) {
                    setEditableValue('');
                    handleSaveValue(labTest.id, resultId, valueId, value);
                } else {
                    setEditableValue(valueId);
                }
                setInputValue({ value: value, for: null });
            }
        };
    const handleInputChange = useCallback((ec) => {
        setInputValue({ value: ec.target.value, for: ec.target.name });
    }, []);

    const commonPath = `/${params.clinicId}/patient/${params?.id}/laboratory/result`;

    const handleSelectOptions = (option, resultId) => {
        if (option?.value === 0) {
            history.push({
                pathname: `${commonPath}/form/${resultsGroup?.id}/${resultId}`,
                state: { ...location.state }
            });
        } else if (option?.value === 1) {
            setDelResultId(resultId);
            setDialog(true);
        }
    };
    const handleDelete = () => {
        dispatch(LabActions.deleteLaboratoryResult(delResultId, labTest.id));
        setDialog(false);
    };
    const Columns = useCallback(({ data: result, inputValue, editableValue }) => {
        return (
            <>
                <TableItem>
                    <TableItemHeader ableItemHeader>
                        {moment(result.issued_datetime).format('jYY/jMM/jDD  HH:mm')}
                    </TableItemHeader>
                    {result.values.map((v) => (
                        <TableItemValue key={`result-value-${v.id || v.factor_id}`}>
                            {editableValue === v.id ? (
                                <input
                                    data-testid="inputValue"
                                    value={
                                        inputValue.for === `${v.id}` && inputValue.value
                                            ? inputValue.value
                                            : v.value
                                    }
                                    onChange={handleInputChange}
                                    className={classes.input}
                                    onBlur={handleToggleValueInput({
                                        valueId: v.id,
                                        resultId: result.id,
                                        value: inputValue.value,
                                        editableValue
                                    })}
                                    name={v.id}
                                />
                            ) : (
                                <Typography
                                    className={classes.value}
                                    color={v.abnormal ? 'error' : undefined}
                                    data-testid="labTableValue"
                                    onClick={handleToggleValueInput({
                                        valueId: v.id,
                                        resultId: result.id,
                                        value: v?.value,
                                        editableValue: editableValue
                                    })}
                                >
                                    {v?.value}
                                </Typography>
                            )}
                        </TableItemValue>
                    ))}

                    <TableItemValue className={classes.optionsContainer}>
                        <Options
                            top={5}
                            right={10}
                            width={'20px'}
                            options={options}
                            handleSelect={(option) => handleSelectOptions(option, result.id)}
                            disabled={notEditClinical}
                        />
                    </TableItemValue>
                </TableItem>
            </>
        );
        // eslint-disable-next-line
    }, []);

    const handleNavigateNext = () => {
        const results = labTest.results.map((v) => `${v.id}`);
        const length = Math.round(Math.floor(tableRef?.current?.scrollLeft) / 250);
        if (length < results.length - 1) {
            tableRef.current.scrollTo(250 * (length + 1), 0);
            setScrollLeft(tableRef?.current?.scrollLeft);
        }
    };

    const handleNavigatePrev = () => {
        const length = Math.round(Math.floor(tableRef?.current?.scrollLeft) / 250);
        if (length > 0) {
            tableRef.current.scrollTo(250 * (length - 1), 0);
            setScrollLeft(tableRef?.current?.scrollLeft);
        }
    };

    return (
        <>
            <AlertDialog
                open={dialog}
                alertText={messages.alerts.deleteLaboratoryTest}
                cancelText={messages.no}
                confirmText={messages.yes}
                handleAction={handleDelete}
                handleClose={() => setDialog(false)}
            />
            <div className={classes.container}>
                <IconButton className={classes.navigateNext} onClick={handleNavigateNext}>
                    <ChevronRight color="inherit" className={classes.icons} />
                </IconButton>
                <IconButton className={classes.navigatePrev} onClick={handleNavigatePrev}>
                    <ChevronLeft color="inherit" className={classes.icons} />
                </IconButton>
                <Table
                    innerRef={tableRef}
                    className={classes.table}
                    key={`lab-test-${labTest?.id}`}
                >
                    <TableItem isFirstColumn>
                        <TableItemHeader>{labTest?.title}</TableItemHeader>
                        {labTest?.factors?.map((factor) => (
                            <TableItemValue key={`factor-${labTest?.id}-${factor.id}`}>
                                {factor?.name}
                            </TableItemValue>
                        ))}
                        <TableItemValue>{}</TableItemValue>
                    </TableItem>

                    {labTest?.results?.map((v) => (
                        <>
                            <Columns
                                data={v}
                                key={`test-result-${v.id}`}
                                inputValue={inputValue}
                                editableValue={editableValue}
                            />
                        </>
                    ))}
                </Table>
            </div>
        </>
    );
}

Rows.propTypes = {
    data: PropTypes.object,
    resultScreenPath: PropTypes.string,
    handleSaveValue: PropTypes.func
};

export default memo(Rows);
